import { Component, Element, Event, EventEmitter, Listen, Prop, State, h } from '@stencil/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { AiIconType } from '../../utils/icon-types';
import {
  extendPastSurrogate,
  retreatBeforeTrailingSurrogate,
  REVEAL_DRAIN_S,
  revealStep,
} from '../../utils/utils';

// Inline action tag the LLM places where a button belongs, e.g.
// ``<action name="book_demo"/>``. Resolved against the message's actions prop
// and rendered as an inline chip in place.
const ACTION_TAG_RE = /<action\s+name=["']([a-zA-Z0-9_-]+)["']\s*(?:\/>|>\s*<\/action>)/gi;

// processMarkdown reparses all accumulated markdown, so syncing the DOM on
// every animation frame does quadratic work on long answers. Revealed text is
// batched and flushed to the DOM at most this often (~30 fps).
const MARKDOWN_SYNC_MIN_MS = 32;

function escapeHtml(value: string): string {
  return (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

@Component({
  tag: 'biel-message',
  styleUrl: 'biel-message.css',
  shadow: true,
})
export class BielMessage {
  @Prop() messageId?: string;
  @Prop() sender: 'user' | 'ai';
  @Prop() text: string;
  @Prop() sources?: { title: string; url: string }[];
  // Every action configured on the project (from getProjectDetails). The LLM
  // emits inline <action name="x"/> tags in the message text; we resolve them
  // to chips against this list, live as the tag streams in. There is no
  // per-message actions field — the tag carried in the text is the source of
  // truth, here and for historical conversations.
  @Prop() availableActions?: { name: string; button_text: string; url: string }[];
  @Prop() feedback?: 0 | 1;
  @Prop() hideFeedback: boolean;
  @Prop() bielId: string;
  @Prop() isPartial: boolean;
  @Prop() partialId: number;
  @Prop() sourcesText: string = 'Sources';
  @Prop() aiIcon: AiIconType = 'biel';
  @Prop() hideAvatars: boolean = false;
  @Prop() hideSources: boolean = false;
  @Prop() assistantLabel: string = 'Assistant';
  // Paced reveal of streamed text (typewriter feel on fast models). Disabled
  // per user preference for reduced motion.
  @Prop() smoothStreaming: boolean = true;

  @State() content: string;
  @State() rawContent: string = '';
  @State() feed: 0 | 1;
  @State() src: { title: string; url: string }[];
  // Footer local UI state.
  @State() showSources: boolean = false;
  @State() copied: boolean = false;
  // A streamed message stays isPartial while its content is replaced in place;
  // completion is signalled by partialMessageComplete. Historical/preloaded
  // messages arrive with isPartial=false and are complete from the start.
  @State() completed: boolean = false;

  @Element() el: HTMLElement;

  @Event() scrollToBottom: EventEmitter<{ bielId: string }>;
  // Same name and payload as the retired biel-feedback component so the
  // body-level listener in biel-bot keeps working unchanged.
  @Event() feedbackSent: EventEmitter<{ bielId: string; messageId: string; reaction: 0 | 1 }>;

  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  // Streamed characters not yet revealed, drained by the rAF loop below.
  private revealBacklog: string = '';
  private revealCarry: number = 0;
  private revealRafId: number | null = null;
  private lastRevealTs: number = 0;
  // Characters already revealed by pacing but not yet flushed to the DOM
  // (flushes are throttled to MARKDOWN_SYNC_MIN_MS).
  private pendingReveal: string = '';
  private lastMarkdownTs: number = 0;
  // Completion payload held back until the backlog finishes draining, so
  // sources/feedback appear after the last characters, not before.
  private pendingCompletion: {
    text?: string;
    feedback?: 0 | 1;
    sources?: { title: string; url: string }[];
  } | null = null;

  private get useSmoothStreaming(): boolean {
    return (
      this.smoothStreaming &&
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function' &&
      !(
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
    );
  }

  @Listen('addMessageText', { target: 'body' })
  handleTextUpdate(event: CustomEvent<{ text: string; id: number; bielId: string }>) {
    if (
      this.bielId === event.detail.bielId &&
      this.isPartial &&
      this.partialId === event.detail.id
    ) {
      if (this.useSmoothStreaming) {
        this.revealBacklog += event.detail.text;
        this.startRevealLoop();
      } else {
        // Flush queued text first so switching away from smooth streaming
        // mid-stream (attribute change, reduced-motion toggle) can't place
        // this delta ahead of older, still-queued text.
        this.finishRevealNow(event.detail.text);
      }
    }
  }

  private startRevealLoop() {
    if (this.revealRafId !== null) return;
    this.lastRevealTs = performance.now();
    this.revealRafId = requestAnimationFrame(this.revealTick);
  }

  private revealTick = (ts: number) => {
    this.revealRafId = null;
    // Animation frames are throttled or paused in hidden tabs, so pacing
    // there only builds lag; reveal everything and stay caught up.
    if (typeof document !== 'undefined' && document.hidden) {
      this.finishRevealNow();
      return;
    }
    const dt = Math.min((ts - this.lastRevealTs) / 1000, 0.1);
    this.lastRevealTs = ts;
    const maxLag = this.pendingCompletion ? REVEAL_DRAIN_S : undefined;
    const step = revealStep(this.revealBacklog.length, dt, this.revealCarry, maxLag);
    this.revealCarry = step.carry;
    if (step.chars > 0) {
      let end = extendPastSurrogate(this.revealBacklog, step.chars);
      // While the stream is live, a trailing high surrogate waits for its low
      // half; once completion arrived no more text is coming, so flush it.
      if (!this.pendingCompletion) {
        end = retreatBeforeTrailingSurrogate(this.revealBacklog, end);
      }
      if (end > 0) {
        this.pendingReveal += this.revealBacklog.slice(0, end);
        this.revealBacklog = this.revealBacklog.slice(end);
      }
    }
    const drained = this.revealBacklog.length === 0;
    if (this.pendingReveal && (drained || ts - this.lastMarkdownTs >= MARKDOWN_SYNC_MIN_MS)) {
      this.processMarkdown(this.pendingReveal);
      this.pendingReveal = '';
      this.lastMarkdownTs = ts;
    }
    if (!drained) {
      this.revealRafId = requestAnimationFrame(this.revealTick);
    } else if (this.pendingCompletion) {
      this.applyCompletion(this.pendingCompletion);
      this.pendingCompletion = null;
    }
  };

  // Reveal everything immediately, bypassing pacing: rAF pacing is pointless
  // in a hidden tab, and the non-smooth path must never run ahead of text
  // still queued here. Applies any completion held back for the drain.
  private finishRevealNow(extra: string = '') {
    if (this.revealRafId !== null) {
      cancelAnimationFrame(this.revealRafId);
      this.revealRafId = null;
    }
    const text = this.pendingReveal + this.revealBacklog + extra;
    this.pendingReveal = '';
    this.revealBacklog = '';
    this.revealCarry = 0;
    if (text) {
      this.processMarkdown(text);
    }
    if (this.pendingCompletion) {
      this.applyCompletion(this.pendingCompletion);
      this.pendingCompletion = null;
    }
  }

  private applyCompletion(payload: {
    text?: string;
    feedback?: 0 | 1;
    sources?: { title: string; url: string }[];
  }) {
    // The completed payload is the authoritative answer. Besides recovering
    // any delayed/missed streamed suffix, replacing the whole body gives
    // nested custom elements (notably code-snippet) one clean, stable mount
    // after the stream has stopped changing beneath them.
    this.replaceContent(payload.text);
    this.feed = payload.feedback;
    this.src = payload.sources;
    this.completed = true;
  }
  @Listen('partialMessageComplete', { target: 'body' })
  handlePartialMessageComplete(
    event: CustomEvent<{
      partialId: number;
      bielId: string;
      msg: {
        text: string;
        sender: 'user' | 'ai';
        messageId?: string;
        feedback?: 0 | 1;
        sources?: { title: string; url: string }[];
        isPartial: boolean;
        partialId?: number;
      };
    }>
  ) {
    // The parent can already have re-rendered this message with
    // isPartial=false by the time the delayed completion event is delivered.
    // Match the stable bot + stream identity instead of transient UI state.
    if (this.bielId === event.detail.bielId && this.partialId === event.detail.partialId) {
      const payload = {
        text: event.detail.msg.text,
        feedback: event.detail.msg.feedback,
        sources: event.detail.msg.sources,
      };
      if (this.revealBacklog.length > 0 || this.pendingReveal.length > 0) {
        this.pendingCompletion = payload;
        this.startRevealLoop();
      } else {
        this.applyCompletion(payload);
      }
    }
  }

  /**
   * Replace the streamed draft with the server's completed text. The final
   * payload may differ from the deltas and is also the safest point to remount
   * custom elements that were updated repeatedly while Markdown was partial.
   */
  replaceContent(text?: string): void {
    if (!text) return;
    this.rawContent = '';
    this.processMarkdown(text, true);
  }

  componentWillLoad() {
    this.feed = this.feedback;
    this.src = this.sources;
    this.completed = !this.isPartial;
    this.processMarkdown(this.text);
  }

  disconnectedCallback() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
    if (this.revealRafId !== null) {
      cancelAnimationFrame(this.revealRafId);
      this.revealRafId = null;
    }
  }

  // A completed answer is one that finished streaming (or was preloaded).
  private get isComplete(): boolean {
    return this.completed || !this.isPartial;
  }

  private toggleSources() {
    this.showSources = !this.showSources;
  }

  private hostnameFrom(url: string): string {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  private handleReaction(reaction: 0 | 1) {
    if (this.messageId && this.feed !== reaction) {
      this.feed = reaction;
      this.feedbackSent.emit({
        bielId: this.bielId,
        messageId: this.messageId,
        reaction,
      });
    }
  }

  // Copy the raw markdown (pre-render) so code blocks and links survive. For
  // streamed messages rawContent accumulates the deltas; for historical ones it
  // is seeded from the original text in componentWillLoad. Either way it is the
  // source markdown, never the rendered HTML.
  private async copyAnswer() {
    const text = this.rawContent || this.text || '';
    let ok = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      ok = this.execCommandCopy(text);
    }
    if (ok) {
      this.copied = true;
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => {
        this.copied = false;
        this.copyResetTimer = null;
      }, 1500);
    }
  }

  private execCommandCopy(text: string): boolean {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      if (typeof textarea.select === 'function') {
        textarea.select();
      }
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }

  componentDidLoad() {
    const visibleDiv = this.el.shadowRoot.querySelector('#biel-message') as HTMLElement;
    // The first Markdown pass happens in componentWillLoad, before the target
    // div exists. Seed it after mount for both historical and partial messages.
    if (this.content) {
      visibleDiv.innerHTML = this.content;
    }
    if (!this.isPartial || this.sender === 'user') {
      this.scrollToBottom.emit({ bielId: this.bielId });
    }
  }

  /**
   * Replace each inline ``<action name="x"/>`` tag with an anchor styled as a
   * chip, resolving the name against the project's actions. Unknown/unresolved
   * tags are removed so no raw markup leaks. Runs before Markdown parsing so
   * the anchor survives marked + DOMPurify (which both keep plain <a> elements).
   */
  renderInlineActions(text: string): string {
    const byName = new Map((this.availableActions || []).map((a) => [a.name, a]));
    return text.replace(ACTION_TAG_RE, (_match, name) => {
      const action = byName.get(name);
      if (!action) return '';
      const label = escapeHtml(action.button_text);
      const href = escapeHtml(action.url);
      return `<a class="biel-inline-action" href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    });
  }

  processMarkdown(fragment: string, replaceAll: boolean = false) {
    this.rawContent += fragment;
    const withActions = this.renderInlineActions(this.rawContent);
    const markedContent = marked.parse(withActions, { breaks: true }) as string;
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    let html: string;
    try {
      html = DOMPurify.sanitize(markedContent, { ADD_ATTR: ['target', 'rel'] });
    } finally {
      DOMPurify.removeHook('afterSanitizeAttributes');
    }
    const htmlSnippet = html
      .replace(/<pre><code/g, `<code-snippet`)
      .replace(/<\/code><\/pre>/g, '</code-snippet>')
      .replace(
        /<code-snippet([^>]*)>\s*([\s\S]*?)\s*<\/code-snippet>/g,
        (_, attributes, content) => {
          const sanitizedContent = content.trim().replace(/"/g, '&quot;');
          const code = `code="${sanitizedContent}"`;
          return `<code-snippet${attributes.replace(/class="(.*?)"/, (_, language) => (language ? ` language="${language}"` : ''))} ${DOMPurify.sanitize(code)}></code-snippet>`;
        }
      )
      .replace(
        /<table>([\s\S]*?)<\/table>/g,
        '<div class="biel-table-container"><table>$1</table></div>'
      );

    const visibleDiv = this.el.shadowRoot.querySelector('#biel-message') as HTMLElement;
    const newElements = this.convertToElement(htmlSnippet);

    if (!visibleDiv) {
      // componentWillLoad precedes the first render. Keep the initial parsed
      // body so componentDidLoad can seed the real container.
      this.content = htmlSnippet;
      return;
    }

    if (replaceAll) {
      this.content = htmlSnippet;
      // componentWillLoad runs before this node exists; completion runs after
      // mount and can replace it immediately.
      if (visibleDiv) visibleDiv.innerHTML = htmlSnippet;
    } else if (this.isPartial) {
      this.deepCompareAndReplace(visibleDiv, newElements);
    } else {
      this.content = htmlSnippet;
    }
  }

  processTable(element: HTMLElement) {
    const tableElements = element.querySelectorAll('table');
    tableElements.forEach((tableElement) => {
      const table = document.createElement('biel-table');
      table.setAttribute('tableData', tableElement.outerHTML);
      tableElement.replaceWith(table);
    });
  }

  convertToElement(content: string): HTMLElement {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    return tempDiv;
  }

  deepCompareAndReplace(element1: HTMLElement, element2: HTMLElement): void {
    if (!element1 || !element2) return;

    if (element2.attributes.length > 0) {
      this.synchronizeAttributes(element1, element2);
    }

    // A custom element owns its rendered descendants. Recursing into those
    // nodes races the element's async upgrade/render lifecycle; in Stencil's
    // scoped/shadow fallback it can mistake the component's injected <style>
    // and template nodes for Markdown content and move/remove them. Updating
    // the host attributes is sufficient for its @Prop watchers to re-render.
    if (element1.tagName.includes('-')) {
      return;
    }

    const childNodes1 = Array.from(element1.childNodes);
    const childNodes2 = Array.from(element2.childNodes);

    const maxLength = Math.max(childNodes1.length, childNodes2.length);

    for (let i = 0; i < maxLength; i++) {
      const child1 = childNodes1[i];
      const child2 = childNodes2[i];

      if (!child1 && child2) {
        element1.appendChild(this.cloneMessageNode(child2));
      } else if (child1 && !child2) {
        element1.removeChild(child1);
      } else if (child1 && child2) {
        if (!this.areNodesEqual(child1, child2)) {
          element1.replaceChild(this.cloneMessageNode(child2), child1);
        } else if (child1.nodeType === Node.ELEMENT_NODE) {
          this.deepCompareAndReplace(child1 as HTMLElement, child2 as HTMLElement);
        }
      }
    }
  }

  private cloneMessageNode(node: Node): Node {
    // Detached custom elements may finish an async Stencil render before this
    // reconciler clones them. Their light-DOM fallback can then include the
    // component stylesheet/template. Clone only the host and its Markdown
    // attributes; the connected element will render its own internals.
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName.includes('-')) {
      return node.cloneNode(false);
    }
    return node.cloneNode(true);
  }

  areNodesEqual(node1: Node, node2: Node): boolean {
    if (node1.nodeType !== node2.nodeType) return false;

    if (node1.nodeType === Node.TEXT_NODE) {
      return node1.textContent === node2.textContent;
    }

    if (node1.nodeType === Node.ELEMENT_NODE && node2.nodeType === Node.ELEMENT_NODE) {
      const element1 = node1 as HTMLElement;
      const element2 = node2 as HTMLElement;

      return element1.tagName === element2.tagName;
    }
    return false;
  }

  synchronizeAttributes(element1: HTMLElement, element2: HTMLElement): void {
    const attributes2 = Array.from(element2.attributes).filter((attr) => attr.name !== 'class');

    attributes2.forEach((attr) => {
      if (element1.getAttribute(attr.name) !== attr.value) {
        element1.setAttribute(attr.name, attr.value);
      }
    });
  }

  private renderFooter() {
    const sources = this.src || [];
    const hasSources = sources.length > 0 && !this.hideSources;
    const showThumbs = !this.hideFeedback && !!this.messageId;
    const hasChoice = this.feed === 0 || this.feed === 1;
    const panelId = `biel-sources-panel-${this.messageId || this.partialId || 'x'}`;

    return (
      <div class="biel-message__footer" part="footer">
        <div class="biel-message__footer-row">
          <div class="biel-message__footer-left">
            {hasSources && (
              <button
                class="biel-message__sources-toggle"
                part="sources-toggle"
                type="button"
                aria-expanded={this.showSources ? 'true' : 'false'}
                aria-controls={panelId}
                onClick={() => this.toggleSources()}
              >
                <span>{this.sourcesText}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  class={`biel-message__chevron ${this.showSources ? 'biel-message__chevron--up' : ''}`}
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
          <div class="biel-message__footer-actions" part="actions">
            <button
              class={`biel-message__action ${this.copied ? 'biel-message__action--copied' : ''}`}
              part="copy-button"
              type="button"
              aria-label={this.copied ? 'Copied' : 'Copy answer'}
              onClick={() => this.copyAnswer()}
            >
              {this.copied ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-check"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-copy"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              )}
            </button>
            {showThumbs && (
              <button
                class={`biel-message__action biel-message__thumb ${this.feed === 1 ? 'biel-message__thumb--selected' : ''} ${hasChoice && this.feed !== 1 ? 'biel-message__thumb--muted' : ''}`}
                part="thumb-up"
                type="button"
                aria-label="Helpful"
                aria-pressed={this.feed === 1 ? 'true' : 'false'}
                onClick={() => this.handleReaction(1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-thumbs-up"
                >
                  <path d="M7 10v12" />
                  <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
                </svg>
              </button>
            )}
            {showThumbs && (
              <button
                class={`biel-message__action biel-message__thumb ${this.feed === 0 ? 'biel-message__thumb--selected' : ''} ${hasChoice && this.feed !== 0 ? 'biel-message__thumb--muted' : ''}`}
                part="thumb-down"
                type="button"
                aria-label="Not helpful"
                aria-pressed={this.feed === 0 ? 'true' : 'false'}
                onClick={() => this.handleReaction(0)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-thumbs-down"
                >
                  <path d="M17 14V2" />
                  <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {hasSources && this.showSources && (
          <div class="biel-message__sources-panel" part="sources-panel" id={panelId}>
            {sources.map((source, index) => {
              const host = this.hostnameFrom(source.url);
              return (
                <a
                  class="biel-message__source-card"
                  part="source-card"
                  id={`biel-source-${this.messageId || this.partialId || 'x'}-${index + 1}`}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="biel-message__source-icon"
                    part="source-icon"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                    <path d="M14 2v6h6" />
                    <line x1="16" x2="8" y1="13" y2="13" />
                    <line x1="16" x2="8" y1="17" y2="17" />
                    <line x1="10" x2="8" y1="9" y2="9" />
                  </svg>
                  <span class="biel-message__source-text">
                    <span class="biel-message__source-title" part="source-title">
                      {source.title}
                    </span>
                    {host && (
                      <span class="biel-message__source-host" part="source-host">
                        {host}
                      </span>
                    )}
                  </span>
                </a>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  render() {
    const showFooter = this.sender === 'ai' && this.isComplete && !!this.messageId;
    return (
      <div
        class={`biel-message biel-message--${this.sender} ${this.hideAvatars ? 'biel-message--no-avatar' : ''}`}
        part="container"
      >
        {this.sender === 'ai' && (
          <div class="biel-message__label" part="label">
            • {this.assistantLabel}
          </div>
        )}
        <div class="biel-message__bubble" part="bubble">
          <div class="biel-message__content" part="content">
            <div id="biel-message"></div>
          </div>
          {showFooter && this.renderFooter()}
        </div>
      </div>
    );
  }
}
